export const VISIT_TIME_ZONE = 'Asia/Kolkata';
export const VISIT_TIME_SLOTS = ['10:00 AM', '1:00 PM', '4:00 PM'] as const;

export interface VisitDay {
  isoDate: string;
  label: string;
  slots: (typeof VISIT_TIME_SLOTS)[number][];
}

const SLOT_MINUTES: Record<(typeof VISIT_TIME_SLOTS)[number], number> = {
  '10:00 AM': 10 * 60,
  '1:00 PM': 13 * 60,
  '4:00 PM': 16 * 60,
};

/** Returns the next seven India-local calendar days, omitting Sundays and past/current times today. */
export function getUpcomingVisitDays(now = new Date()): VisitDay[] {
  const local = getIndiaDateTimeParts(now);
  const startUtc = Date.UTC(local.year, local.month - 1, local.day);
  const currentMinutes = local.hour * 60 + local.minute;
  const days: VisitDay[] = [];

  for (let offset = 0; offset < 7; offset += 1) {
    const date = new Date(startUtc + offset * 24 * 60 * 60 * 1000);
    if (date.getUTCDay() === 0) continue;

    const slots = VISIT_TIME_SLOTS.filter(
      (slot) => offset > 0 || SLOT_MINUTES[slot] > currentMinutes,
    );
    if (slots.length === 0) continue;

    const year = date.getUTCFullYear();
    const month = date.getUTCMonth() + 1;
    const day = date.getUTCDate();
    const isoDate = `${year}-${pad2(month)}-${pad2(day)}`;

    days.push({
      isoDate,
      label: formatVisitDate(isoDate),
      slots,
    });
  }

  return days;
}

export function formatVisitDate(isoDate: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(isoDate)) return isoDate;
  const [year, month, day] = isoDate.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  if (date.toISOString().slice(0, 10) !== isoDate) return isoDate;
  const weekday = new Intl.DateTimeFormat('en-GB', { weekday: 'short', timeZone: 'UTC' }).format(date);
  const dayLabel = new Intl.DateTimeFormat('en-GB', { day: '2-digit', timeZone: 'UTC' }).format(date);
  const monthLabel = new Intl.DateTimeFormat('en-GB', { month: 'short', timeZone: 'UTC' }).format(date);
  return `${weekday} ${dayLabel} ${monthLabel}`;
}

export function slotInstruction(
  locale: string,
  dateLabel: string,
  time: string,
): string {
  const language = new Intl.Locale(locale).language;
  const script = new Intl.Locale(locale).maximize().script;
  if (language === 'hi' && script === 'Deva') {
    return `मुझे ${dateLabel} को ${time} का समय चाहिए।`;
  }
  if (language === 'hi' && script === 'Latn') {
    return `Mujhe ${dateLabel} ko ${time} ka slot chahiye.`;
  }
  return `I’d prefer ${dateLabel} at ${time}.`;
}

function getIndiaDateTimeParts(date: Date): { year: number; month: number; day: number; hour: number; minute: number } {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: VISIT_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  const values = Object.fromEntries(parts.map((part) => [part.type, Number(part.value)]));
  return {
    year: values.year,
    month: values.month,
    day: values.day,
    hour: values.hour,
    minute: values.minute,
  };
}

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}
